import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Switch,
} from '@racefront/shadcn-ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { APP_ROUTES } from '../../../../src/constants/routes'
import { useAuth } from '../../data/hooks/auth'
import useUserAccount from '../../data/hooks/user'
import { useThemeStore } from '../../data/stores/theme.store'
import { UserForm, UserFormRef } from '../user-form/user-form'
import { AlertCircle, Loader2, LogOut, User2, ChevronLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { IncompleteProfileAlert } from './incomplete-profile-alert'
import { useSearchParams } from 'react-router-dom'
import { VerificationModal } from './verification-modal'
import { GoToAppLinks } from './go-to-app-links'

type RNWindow = Window & { ReactNativeWebView?: { postMessage: (msg: string) => void } }
const getRNWebView = () => (window as RNWindow).ReactNativeWebView

export default function Account() {
  const intl = useIntl()
  const [params] = useSearchParams()
  const { data, isLoading: isUserLoading, error: userError, fetchUser } = useUserAccount()
  const { theme } = useThemeStore()
  const {
    updateAccount,
    updateAccountLoading,
    updateAccountSuccess,
    updateAccountError,
    updateAccountData,
    verifyUpdateField,
    verifyUpdateFieldError,
    verifyUpdateFieldLoading,
    verifyUpdateFieldSuccess,
    deleteAccount,
    deleteAccountLoading,
    deleteAccountError,
    deleteAccountSuccess,
  } = useAuth()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
  const [isProfileCompleteDialogOpen, setIsProfileCompleteDialogOpen] = useState(false)
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false)
  const [verificationType, setVerificationType] = useState<'email' | 'phone_number'>('email')
  const [signOutFromAllDevices, setSignOutFromAllDevices] = useState(false)
  const formRef = useRef<UserFormRef>(null)

  const redirectAfterUpdate = params.get('redirectAfterUpdate')
  const redirectUri = params.get('redirectUri')

  const handleDeleteAccount = () => {
    deleteAccount()
  }

  const handleLogout = useCallback(() => {
    setIsLogoutDialogOpen(false)

    setTimeout(() => {
      const rnWebView = getRNWebView()
      if (rnWebView) {
        rnWebView.postMessage('LOG_OUT')
      } else {
        window.location.replace(APP_ROUTES.SignOut + (signOutFromAllDevices ? '?global=true' : ''))
      }
    }, 300)
  }, [signOutFromAllDevices])

  const handleRedirectBack = () => {
    if (redirectUri) {
      window.location.assign(redirectUri)
    }
    if (redirectAfterUpdate) {
      window.location.replace(redirectAfterUpdate)
    }
  }

  const handleStayOnPage = () => {
    setIsProfileCompleteDialogOpen(false)
  }

  const handleVerificationConfirm = (code: string) => {
    verifyUpdateField(code, verificationType)
  }

  useEffect(() => {
    const medium =
      verificationType === 'email'
        ? intl.formatMessage({ id: 'account.verification.medium.email', defaultMessage: 'email' })
        : intl.formatMessage({ id: 'account.verification.medium.phone', defaultMessage: 'phone number' })

    if (verifyUpdateFieldSuccess) {
      toast.success(
        <FormattedMessage
          id="account.verification.success"
          defaultMessage="{medium} verified successfully"
          values={{ medium }}
        />,
      )
      setIsVerificationModalOpen(false)
      formRef.current?.reset()
      fetchUser()
    }

    if (verifyUpdateFieldError) {
      toast.error(
        <FormattedMessage
          id="account.verification.error"
          defaultMessage="Failed to verify {medium}"
          values={{ medium }}
        />,
      )
    }
  }, [verificationType, verifyUpdateFieldError, verifyUpdateFieldSuccess, fetchUser, intl])

  useEffect(() => {
    if (deleteAccountSuccess) {
      toast.success(<FormattedMessage id="account.delete_success" defaultMessage="Account deleted successfully" />)

      setIsDeleteDialogOpen(false)

      handleLogout()
    }

    if (deleteAccountError) {
      toast.error(<FormattedMessage id="account.delete_error" defaultMessage="Failed to delete account" />)
    }
  }, [deleteAccountError, deleteAccountSuccess, handleLogout])

  useEffect(() => {
    if (updateAccountSuccess) {
      if (!updateAccountData?.requireEmailVerification && !updateAccountData?.requirePhoneVerification) {
        toast.success(
          <FormattedMessage id="account.update_success" defaultMessage="Account details updated successfully" />,
        )
      }

      // Check if profile is now complete and redirect URL is provided
      if (redirectAfterUpdate && updateAccountData?.account?.profileComplete) {
        setIsProfileCompleteDialogOpen(true)
      }

      if (updateAccountData?.requireEmailVerification || updateAccountData?.requirePhoneVerification) {
        // Determine which type of verification is needed
        const verifyType = updateAccountData.requireEmailVerification ? 'email' : 'phone_number'
        setVerificationType(verifyType)
        setIsVerificationModalOpen(true)
      }
      formRef.current?.reset()
      fetchUser()
    }
    if (updateAccountError) {
      toast.error(<FormattedMessage id="account.update_error" defaultMessage="Failed to update account details" />)
    }
  }, [updateAccountSuccess, updateAccountError, redirectAfterUpdate, updateAccountData, fetchUser])

  const handleBackToRedirect = () => {
    if (redirectUri) {
      window.location.replace(redirectUri)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="bg-background flex-none border-b px-4 py-2 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={`/logo_${theme}.png`} alt="logo" width={80} height={80} />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="overflow-hidden rounded-full">
                  <User2 className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <p className="truncate">
                    {data?.firstname && data?.lastname ? `${data.firstname} ${data.lastname}` : data?.email}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsLogoutDialogOpen(true)}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <FormattedMessage id="account.logout" defaultMessage="Logout" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {redirectUri && (
            <Button className="mb-3" variant="link" size="sm" onClick={handleBackToRedirect}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              <FormattedMessage id="common.back" defaultMessage="Back to RaceID" />
            </Button>
          )}
        </div>
      </header>

      <div className="flex-1">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold">
                <FormattedMessage id="account.title" defaultMessage="Account Details" />
              </h1>
              <p>
                <FormattedMessage
                  id="account.description"
                  defaultMessage="You can check or modify your account details."
                />
              </p>
            </div>

            <GoToAppLinks />
          </div>
          <IncompleteProfileAlert userData={data} formRef={formRef} />
          <div className="mt-10">
            {isUserLoading && !data ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : userError && !data ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>
                  <FormattedMessage id="account.fetch_error.title" defaultMessage="Couldn't load your account" />
                </AlertTitle>
                <AlertDescription>
                  <p>
                    <FormattedMessage
                      id="account.fetch_error.description"
                      defaultMessage="Something went wrong while loading your account details."
                    />
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchUser()}>
                    <FormattedMessage id="account.fetch_error.retry" defaultMessage="Try again" />
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <UserForm ref={formRef} user={data} onSave={updateAccount} isLoading={updateAccountLoading} />
            )}
          </div>

          {/* Danger Zone */}
          <div className="mt-6">
            <div className="border-destructive/50 bg-destructive/5 rounded-xl border p-6 shadow-sm">
              <div className="mb-4">
                <h3 className="text-destructive text-lg font-semibold">
                  <FormattedMessage id="account.danger_zone" defaultMessage="Danger Zone" />
                </h3>
                <p className="text-muted-foreground text-sm">
                  <FormattedMessage
                    id="account.danger_zone.description"
                    defaultMessage="Irreversible actions that will permanently affect your account."
                  />
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-card-foreground font-medium">
                    <FormattedMessage id="account.delete_account" defaultMessage="Delete Account" />
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    <FormattedMessage
                      id="account.delete_account.description"
                      defaultMessage="Permanently delete your account and all associated data."
                    />
                  </p>
                </div>
                <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)} size="sm">
                  <FormattedMessage id="account.delete_account.button" defaultMessage="Delete Account" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      {isLogoutDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background mx-4 w-full max-w-md rounded-lg p-6 shadow-lg">
            <h2 className="mb-2 text-lg font-semibold">
              <FormattedMessage id="account.logout_dialog.title" defaultMessage="Sign out" />
            </h2>
            <p className="text-muted-foreground mb-4">
              <FormattedMessage
                id="account.logout_dialog.description"
                defaultMessage="Are you sure you want to logout from your account?"
              />
            </p>
            <div className="mb-6 flex items-center justify-between space-x-3 rounded-lg border p-3">
              <label
                htmlFor="signOutAllDevices"
                className="text-muted-foreground flex-1 cursor-pointer text-xs leading-tight"
              >
                <FormattedMessage
                  id="account.logout_dialog.sign_out_all_devices"
                  defaultMessage="Sign out from all devices (including Pace App)"
                />
              </label>
              <Switch
                id="signOutAllDevices"
                checked={signOutFromAllDevices}
                onCheckedChange={setSignOutFromAllDevices}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsLogoutDialogOpen(false)}>
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button onClick={handleLogout}>
                <FormattedMessage id="account.logout_dialog.confirm" defaultMessage="Logout" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background mx-4 w-full max-w-md rounded-lg p-6 shadow-lg">
            <h2 className="mb-2 text-lg font-semibold">
              <FormattedMessage id="account.delete_dialog.title" defaultMessage="Delete Account" />
            </h2>
            <p className="text-muted-foreground mb-6">
              <FormattedMessage
                id="account.delete_dialog.description"
                defaultMessage="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed."
              />
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleteAccountLoading}>
                {deleteAccountLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <FormattedMessage id="account.delete_dialog.loading" defaultMessage="Deleting..." />
                  </>
                ) : (
                  <FormattedMessage id="account.delete_dialog.confirm" defaultMessage="Delete Account" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Complete Confirmation Dialog */}
      {isProfileCompleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background mx-4 w-full max-w-md rounded-lg p-6 shadow-lg">
            <h2 className="mb-2 text-lg font-semibold">
              <FormattedMessage id="account.profile_complete_dialog.title" defaultMessage="Profile Completed! 🎉" />
            </h2>
            <p className="text-muted-foreground mb-6">
              <FormattedMessage
                id="account.profile_complete_dialog.description"
                defaultMessage="Your profile is now complete. You will be redirected back to continue where you left off."
              />
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleStayOnPage}>
                <FormattedMessage id="account.profile_complete_dialog.stay" defaultMessage="Stay Here" />
              </Button>
              <Button onClick={handleRedirectBack}>
                <FormattedMessage id="account.profile_complete_dialog.redirect" defaultMessage="Continue" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal for Email/Phone Changes */}
      <VerificationModal
        isOpen={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        verificationType={verificationType}
        preview={verificationType === 'email' ? data?.email : data?.phoneNumber}
        onConfirm={handleVerificationConfirm}
        isLoading={verifyUpdateFieldLoading}
      />
    </div>
  )
}
